import {
  ConnectedClassIndicator,
  deserify,
  theme,
} from '@geneo2-web/shared-ui';
import { useMediaQuery } from '@mui/material';
import {
  McqMultipleContentModel,
  McqSingleContentModel,
  Question,
  TfContentModel,
} from '@protos/content_management/content.db_pb';
import { useClickerAssessmentContext } from '../../../../app/Context/ClickerAssessmentContextProvider';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import { calculateMarksMCQM } from '../../../../utils/functions';
import { convertAnswerOptionstoNumbers } from './ResponseList';

interface IProps {
  activeQuestion?: Question;
  showResponse?: boolean;
}
export const ConnectedClassIndicatorWithDetails = (props: IProps) => {
  const { activeQuestion, showResponse } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    upsertConnectedClassQuestionSessionApiv2,
    unsubscribeQuestionFromFirestore,
    unsubscribeFromFirestore,
    questionSessionData,
    caBaseStationData,
    upsertConnectedClassSessionApiv2,
  } = useClickerAssessmentContext();

  const {
    assessment_content,
    current_question_index,
    clicker_assessment_session_data,
    teacher_assessment_session_details,
    ca_base_station_details,
    student_list,
  } = deserify(useAppSelector((state) => state.clickerAssessment));

  const getOnlineKeyPadsCount = () => {
    const keyPads = caBaseStationData?.keyPads || [];
    const onlineKeyPadsCount = keyPads.filter(
      (keypad) => keypad.isOnline
    ).length;
    const totalKeyPadsCount = caBaseStationData?.keyPads.length || 0;
    return { onlineKeyPadsCount, totalKeyPadsCount };
  };

  const studentList = student_list?.map((student) => {
    const response = questionSessionData?.responses.find(
      (e) => e.keypadSn === student.clickerSerialNo
    );
    return {
      ...student,
      response: response, // Add the response data to each student object
    };
  });

  const totalResponseTime =
    studentList?.reduce(
      (acc, student) => acc + (student.response?.timeSpent || 0),
      0
    ) || 0;
  const totalAnswered =
    studentList?.filter((student) => student.response).length || 0;

  const averageTime = totalAnswered
    ? Math.round((60 * totalResponseTime) / totalAnswered)
    : 0;

  const maxMarks = activeQuestion?.question
    ? activeQuestion?.question.model.value?.commonQuestionContent?.marks.reduce(
        (acc, curr) => acc + curr,
        0
      ) || 0
    : 0;
  const totalCorrectAnswersForMcqM =
    activeQuestion?.question?.model.case === 'mcqMultipleContentModel'
      ? studentList?.filter((student) => {
          const response = student.response?.response;
          return (
            response &&
            calculateMarksMCQM(activeQuestion, {
              answer: response.split(''),
            }) === maxMarks
          );
        }).length
      : undefined;

  const correctAnswer =
    activeQuestion?.question?.model.case === 'tfContentModel' ||
    activeQuestion?.question?.model.case === 'mcqSingleContentModel' ||
    activeQuestion?.question?.model.case === 'mcqMultipleContentModel'
      ? activeQuestion.question.model.value.correct
      : undefined;

  const totalCorrectAnswers =
    studentList?.filter(
      (student) =>
        student.response?.response &&
        convertAnswerOptionstoNumbers(student.response?.response) ===
          correctAnswer
    ).length || 0;

  const totalMarksObtainedForMCQM =
    activeQuestion?.question?.model.case === 'mcqMultipleContentModel'
      ? studentList?.reduce((acc, curr) => {
          const response = curr.response?.response;
          if (response) {
            const marks =
              calculateMarksMCQM(activeQuestion, {
                answer: response.split(''),
              }) ?? 0;
            return acc + marks;
          }
          return acc;
        }, 0)
      : undefined;

  const averageMarksforMcqM =
    totalMarksObtainedForMCQM && totalAnswered
      ? (totalMarksObtainedForMCQM / totalAnswered).toFixed(2)
      : 0;

  const percentageMarksForMcqM =
    totalMarksObtainedForMCQM && totalAnswered && maxMarks
      ? Math.round(
          (totalMarksObtainedForMCQM / (totalAnswered * maxMarks)) * 100
        )
      : 0;

  const averageMarks = totalAnswered
    ? ((totalCorrectAnswers / totalAnswered) * maxMarks).toFixed(2)
    : 0;

  const percentageMarks = totalAnswered
    ? Math.round((totalCorrectAnswers / totalAnswered) * 100)
    : 0;

  const getCorrectCount = () => {
    const { onlineKeyPadsCount } = getOnlineKeyPadsCount();
    const correctCount =
      totalCorrectAnswersForMcqM !== undefined
        ? totalCorrectAnswersForMcqM
        : totalCorrectAnswers;

    // If everyone answered, always show the correct count even when responses are hidden
    if (totalAnswered === onlineKeyPadsCount) return correctCount;

    // If responses are allowed to be shown, return the count even if not everyone answered
    if (showResponse === true) return correctCount;

    // Otherwise keep it hidden
    return undefined;
  };

  return (
    <ConnectedClassIndicator
      variant={isMobile ? 'connectedmobile2' : 'connected'}
      fullwidth
      isOnline={caBaseStationData?.isOnline}
      totalJoined={getOnlineKeyPadsCount().onlineKeyPadsCount}
      totalStudents={getOnlineKeyPadsCount().totalKeyPadsCount}
      totalAnswered={totalAnswered}
      // correct={2}
      correct={getCorrectCount()}
      averageTime={averageTime}
      averagemarks={
        totalMarksObtainedForMCQM !== undefined
          ? percentageMarksForMcqM
          : percentageMarks
      }
    />
  );
};
